import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Pencil } from 'lucide-react'

export interface PromptDialogProps {
  isOpen: boolean
  title: string
  message?: string
  inputLabel?: string
  placeholder?: string
  initialValue?: string
  confirmText?: string
  cancelText?: string
  /** return a message to block the submit */
  validate?: (value: string) => string | null
  onConfirm: (value: string) => void
  onClose: () => void
}

/** window.prompt, but in the design system: the version note on Save, the name of a new API key. */
export function PromptDialog({
  isOpen,
  title,
  message,
  inputLabel,
  placeholder,
  initialValue = '',
  confirmText = 'Save',
  cancelText = 'Cancel',
  validate,
  onConfirm,
  onClose,
}: PromptDialogProps) {
  const [value, setValue] = useState(initialValue)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const id = useId()

  useEffect(() => {
    if (!isOpen) return
    setValue(initialValue)
    setError(null)
    const t = setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
    return () => clearTimeout(t)
  }, [isOpen, initialValue])

  if (!isOpen) return null
  const submit = () => {
    const trimmed = value.trim()
    const problem = validate ? validate(trimmed) : trimmed ? null : 'Required'
    if (problem) {
      setError(problem)
      return
    }
    onConfirm(trimmed)
    onClose()
  }
  return createPortal(
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-2xl animate-pop dark:border-slate-700 dark:bg-surface-dark"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-full bg-primary-soft p-2.5">
            <Pencil className="text-primary" size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id={`${id}-title`} className="text-sm font-semibold text-default">
              {title}
            </h3>
            {message && <p className="mt-1.5 text-xs text-muted">{message}</p>}
          </div>
        </div>
        <div className="mt-4">
          <label htmlFor={`${id}-input`} className="label">
            {inputLabel ?? title}
          </label>
          <input
            id={`${id}-input`}
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submit()
              } else if (e.key === 'Escape') onClose()
            }}
            placeholder={placeholder}
            className={`input ${error ? 'input-error' : ''}`}
            aria-invalid={Boolean(error)}
          />
          {error && <p className="field-error">{error}</p>}
        </div>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
            {cancelText}
          </button>
          <button type="button" onClick={submit} className="btn btn-primary flex-1">
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
